import React, { useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Info,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

/* =========================
   Reusable UI Components
========================= */

export const StatCard = ({ label, value, tooltip, trend, className = '' }) => (
  <div className={`group relative bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-gray-300 dark:from-gray-800/60 dark:to-gray-800/40 dark:border-gray-700/60 dark:hover:from-gray-800/80 dark:hover:to-gray-800/60 dark:hover:border-gray-600/80 rounded-xl p-4 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 ${className}`}>
    <div className="flex items-start justify-between mb-3">
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
        {label}
        {tooltip && <Info className="w-3 h-3 text-gray-400 cursor-help hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400 transition-colors" />}
      </div>
      {trend !== undefined && (
        <div className={`flex items-center text-xs ${trend >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        </div>
      )}
    </div>
    <div className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">{value}</div>
    {tooltip && (
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-30 whitespace-nowrap shadow-xl">
        {tooltip}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    )}
  </div>
);

export const DataTable = ({ title, icon: Icon, data, isExpandable = false, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  if (!data || data.length === 0) return null;
  
  return (
    <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-gray-300 dark:from-gray-800/60 dark:to-gray-800/40 dark:border-gray-700/60 dark:hover:border-gray-600/80 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
      <div
        className={`px-5 py-4 border-b border-gray-200 dark:border-gray-700/60 ${isExpandable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/60' : ''} transition-colors duration-200`}
        onClick={() => isExpandable && setExpanded((v) => !v)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-500/10 border border-blue-500/20">
              <Icon className="w-4 h-4 text-blue-700 dark:text-blue-400" />
            </div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
          </div>
          {isExpandable && (
            <div className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700/50 transition-colors">
              {expanded ? <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
            </div>
          )}
        </div>
      </div>
      {expanded && (
        <div className="p-5">
          <div className="space-y-4">
            {data.map((row, i) => (
              <div key={i} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700/30 last:border-b-0 hover:bg-gray-100 dark:hover:bg-gray-700/20 rounded px-2 -mx-2 transition-colors duration-200">
                <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">{row.label}</span>
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const TabNavigation = ({ tabs, activeTab, onTabChange }) => (
  <div className="flex flex-wrap gap-2 mb-6">
    {tabs.map(({ id, label, Icon, count }) => (
      <button
        key={id}
        onClick={() => onTabChange(id)}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium transition-all duration-300 border ${
          activeTab === id
            ? 'bg-blue-500 text-white border-blue-400 shadow-lg shadow-blue-500/25 scale-105'
            : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 hover:text-gray-800 hover:border-gray-300 dark:bg-gray-800/60 dark:text-gray-300 dark:border-gray-700/60 dark:hover:bg-gray-700/60 dark:hover:text-gray-200 dark:hover:border-gray-600/80 hover:shadow-md hover:shadow-blue-500/10'
        }`}
      >
        <Icon className="w-4 h-4" />
        {label}
        {count !== undefined && (
          <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
            activeTab === id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {count}
          </span>
        )}
      </button>
    ))}
  </div>
);